import React, { useRef } from 'react';
import firebase from 'firebase';
import { Collections } from '../../firebase/firestore/collections';
import { useUserIsAuthenticated } from '../../app/hooks/useUserIsAuthenticated';

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  // eslint-disable-next-line
  for (let i = 0; i < a.length; i++) {
    // istanbul ignore next
    if (a[i] !== b[i]) return false;
  }
  return true;
}

interface DateRange {
  fromDate: Date;
  toDate: Date;
}

export interface WatcherOptions {
  selectedRecruiters: string[];
  dateRange: DateRange;
  onChange: (isFirstFetch: boolean) => void;
}
export function useAvailabilityWatcher({ selectedRecruiters, dateRange, onChange }: WatcherOptions) {
  const unsubscribeRef = useRef<(() => void) | undefined>();
  const prevRecruiters = useRef<string[]>([]);
  const firstLoad = useRef(true);
  const userIsAuthenticated = useUserIsAuthenticated();

  // ✅ Stabilize dates
  const fromDateStr = dateRange.fromDate.toISOString();
  const toDateStr = dateRange.toDate.toISOString();

  React.useEffect(() => {
    if (!userIsAuthenticated) return;
    if (!selectedRecruiters || selectedRecruiters.length === 0) {
      // istanbul ignore next
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }
      return;
    }

    const recruitersToQuery = selectedRecruiters.slice(0, 9);

    if (!arraysEqual(prevRecruiters.current, recruitersToQuery)) {
      // istanbul ignore next
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }
      prevRecruiters.current = recruitersToQuery;
      firstLoad.current = true;
    }

    const query: firebase.firestore.Query = firebase
      .firestore()
      .collection(Collections.APPOINTMENT_AVAILABILITY)
      .where('userId', 'in', recruitersToQuery)
      .where('fromDate', '>=', new Date(fromDateStr))
      .where('fromDate', '<=', new Date(toDateStr));

    const unsubscribe = query.onSnapshot((snapshot) => {
      const changes = snapshot.docChanges();

      let shouldRefetch = false;

      // eslint-disable-next-line
      for (const change of changes) {
        if (['added', 'removed', 'modified'].includes(change.type)) {
          shouldRefetch = true;
          break;
        }
      }

      if (shouldRefetch) {
        onChange(firstLoad.current);
        firstLoad.current = false;
      }
    });

    unsubscribeRef.current = unsubscribe;
    // eslint-disable-next-line
    return () => {
      // istanbul ignore next
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [userIsAuthenticated, selectedRecruiters, fromDateStr, toDateStr, onChange]);
}
